import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/services/api';
import { HelpCircle, Users, UserPlus, X, Trash2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

// --- Interfaces ---

interface Operation {
    id: number;
    code: string;
    description: string;
    type: string;
    status: string;
    startDateTime: string;
    endDateTime: string;
    latitude: string;
    longitude: string;
    radius: string;
    zone: string;
    agency: string;
    supportServices: string[];
    contactName: string;
    contactPhone: string;
    eventType: string;
    notes: string;
    opcLevel?: string;
    opcScore?: number;
    ownerAgency?: {
        id: number;
        code: string;
        name: string;
    };
}

interface NewOperation {
    code: string;
    description: string;
    type: string;
    status: string;
    startDateTime: string;
    endDateTime: string;
    latitude: string;
    longitude: string;
    radius: string;
    zone: string;
    agency: string;
    supportServices: string[];
    contactName: string;
    contactPhone: string;
    eventType: string;
    notes: string;
    ct1Coordinacion: number;
    ct2Intensidad: number;
    ct3Especializacion: number;
    ct4Proposito: number;
    ct5Extension: number;
    rtRelevancia: string;
    hasMinors: boolean;
    hasWeapons: boolean;
    isCriticalSector: boolean;
}

interface Stats {
    total: number;
    planned: number;
    active: number;
    completed: number;
    cancelled: number;
}

// --- Constants ---


const SUPPORT_SERVICES = [
    { id: 'AMBULANCIA', icon: '🚑' },
    { id: 'BOMBEROS', icon: '🚒' },
    { id: 'DEFENSA_CIVIL', icon: '🛡️' },
    { id: 'ELECTRICIDAD', icon: '⚡' },
    { id: 'GAS', icon: '🔥' },
    { id: 'AGUA', icon: '💧' },
    { id: 'TRANSITO', icon: '🚦' },
    { id: 'AEREO', icon: '🚁' },
];

const EVENT_TYPES = [
    { id: 'CRIMEN_ORGANIZADO' },
    { id: 'DROGAS' },
    { id: 'TRANSITO' },
    { id: 'EVENTO_PUBLICO' },
    { id: 'PROTESTA' },
    { id: 'FENOMENO_FRONTERIZO' },
    { id: 'VIP' },
    { id: 'EMERGENCIA' },
    { id: 'BUSQUEDA' },
    { id: 'PATRULLAJE' },
    { id: 'OTRO' },
];

const AGENCIES = [
    { id: 'PDI' },
    { id: 'CARABINEROS' },
    { id: 'ANI' },
    { id: 'GENDARMERIA' },
    { id: 'MUN-STGO' },
];

const OPC_CRITERIA = {
    ct1: {
        options: [
            { value: 1 },
            { value: 2 },
            { value: 3 },
        ]
    },
    ct2: {
        options: [
            { value: 1 },
            { value: 2 },
            { value: 3 },
        ]
    },
    ct3: {
        options: [
            { value: 1 },
            { value: 2 },
            { value: 3 },
        ]
    },
    ct4: {
        options: [
            { value: 1 },
            { value: 2 },
            { value: 3 },
        ]
    },
    ct5: {
        options: [
            { value: 1 },
            { value: 2 },
            { value: 3 },
        ]
    }
};

// --- Main Component ---

interface OperationMember {
    id: number;
    userId: string;
    username: string;
    fullName: string;
    role: string;
    assignedAt: string;
}

interface SystemUser {
    id: string;
    username: string;
    fullName: string;
    roles?: string[];
}

export default function OperationsPage() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const canManageOperations = user?.roles?.some(r => ['ADMINISTRADOR', 'COORDINADOR', 'SUPERVISOR'].includes(r));

    const [operations, setOperations] = useState<Operation[]>([]);
    const [stats, setStats] = useState<Stats | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [saving, setSaving] = useState(false);

    // Members modal state
    const [showMembersModal, setShowMembersModal] = useState(false);
    const [selectedOpForMembers, setSelectedOpForMembers] = useState<Operation | null>(null);
    const [members, setMembers] = useState<OperationMember[]>([]);
    const [allUsers, setAllUsers] = useState<SystemUser[]>([]);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [addingMember, setAddingMember] = useState(false);
    const [newMemberUserId, setNewMemberUserId] = useState('');
    const [newMemberRole, setNewMemberRole] = useState('OPERADOR');
    const [userSearchText, setUserSearchText] = useState('');

    const [newOp, setNewOp] = useState<NewOperation>({
        code: '',
        description: '',
        type: 'INVESTIGATIVA',
        status: 'PLANIFICADA',
        startDateTime: '',
        endDateTime: '',
        latitude: '-33.4489',
        longitude: '-70.6693',
        zone: 'Santiago Centro',
        radius: '500',
        agency: 'PDI',
        supportServices: [],
        contactName: '',
        contactPhone: '',
        eventType: 'CRIMEN_ORGANIZADO',
        notes: '',
        ct1Coordinacion: 1,
        ct2Intensidad: 1,
        ct3Especializacion: 1,
        ct4Proposito: 1,
        ct5Extension: 1,
        rtRelevancia: 'BAJO',
        hasMinors: false,
        hasWeapons: false,
        isCriticalSector: false
    });

    const calculateOPC = useCallback(() => {
        const ctTotal = newOp.ct1Coordinacion + newOp.ct2Intensidad + newOp.ct3Especializacion + newOp.ct4Proposito + newOp.ct5Extension;

        let multiplier = 1.0;
        if (newOp.rtRelevancia === 'ALTO') multiplier += 0.5;
        if (newOp.rtRelevancia === 'MEDIO') multiplier += 0.2;

        // Additional Critical Factors
        if (newOp.hasMinors) multiplier += 0.3;
        if (newOp.hasWeapons) multiplier += 0.4;
        if (newOp.isCriticalSector) multiplier += 0.3;

        const score = ctTotal * multiplier;

        let level = 'NIVEL_I';
        let color = 'green';
        if (score >= 14) { level = 'NIVEL_III'; color = 'red'; } // Adjusted threshold
        else if (score >= 9) { level = 'NIVEL_II'; color = 'yellow'; }

        return { level, score, color };
    }, [newOp]);

    const opcResult = calculateOPC();

    const loadData = async () => {
        try {
            const endpoint = canManageOperations ? '/api/v1/operations' : '/api/v1/operations/my-assigned';
            const [opsRes, statsRes] = await Promise.all([
                api.get(endpoint),
                api.get('/api/v1/operations/stats')
            ]);
            setOperations(opsRes.data.content || opsRes.data || []);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    // Load members for selected operation
    const openMembersModal = async (op: Operation) => {
        setSelectedOpForMembers(op);
        setShowMembersModal(true);
        setLoadingMembers(true);
        setNewMemberUserId('');
        setNewMemberRole('OPERADOR');
        setUserSearchText('');
        try {
            const [membersRes, usersRes] = await Promise.all([
                api.get(`/api/v1/operations/${op.id}/members`),
                api.get('/api/v1/users').catch(() => ({ data: [] }))
            ]);
            setMembers(membersRes.data || []);
            const users = usersRes.data?.content || usersRes.data || [];
            setAllUsers(users.map((u: any) => ({ id: u.id, username: u.username, fullName: u.fullName || u.full_name || u.username, roles: u.roles || [] })));
        } catch { setMembers([]); setAllUsers([]); }
        finally { setLoadingMembers(false); }
    };

    const addMember = async () => {
        if (!selectedOpForMembers || !newMemberUserId) return;
        setAddingMember(true);
        try {
            await api.post(`/api/v1/operations/${selectedOpForMembers.id}/members`, {
                userId: newMemberUserId,
                role: newMemberRole
            });
            // Reload members
            const res = await api.get(`/api/v1/operations/${selectedOpForMembers.id}/members`);
            setMembers(res.data || []);
            setNewMemberUserId('');
        } catch (err: any) {
            alert(err.response?.data?.error || t('operations.errorAssignUser'));
        } finally { setAddingMember(false); }
    };

    const removeMember = async (userId: string) => {
        if (!selectedOpForMembers || !confirm(t('operations.confirmRemoveUser'))) return;
        try {
            await api.delete(`/api/v1/operations/${selectedOpForMembers.id}/members/${userId}`);
            setMembers(prev => prev.filter(m => m.userId !== userId));
        } catch { alert(t('operations.errorRemoveUser')); }
    };

    const handleCreate = async () => {
        try {
            setSaving(true);
            const payload = {
                ...newOp,
                latitude: parseFloat(newOp.latitude),
                longitude: parseFloat(newOp.longitude),
                radius: parseInt(newOp.radius),
                opcLevel: opcResult.level,
                opcScore: opcResult.score,
                contact: {
                    name: newOp.contactName,
                    phone: newOp.contactPhone
                },
                supportServices: newOp.supportServices.join(',')
            };
            await api.post('/api/v1/operations', payload);
            setShowModal(false);
            loadData();
        } catch (error) {
            alert(t('operations.errorCreate'));
        } finally {
            setSaving(false);
        }
    };

    const generateCode = () => {
        const year = new Date().getFullYear();
        const num = Math.floor(Math.random() * 900) + 100;

        // Set default dates: start = now, end = now + 8 hours
        const now = new Date();
        const endTime = new Date(now.getTime() + 8 * 60 * 60 * 1000);

        const formatDateTime = (d: Date) => {
            const pad = (n: number) => n.toString().padStart(2, '0');
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        };

        setNewOp(prev => ({
            ...prev,
            code: `${prev.agency}-${year}-${num}`,
            startDateTime: formatDateTime(now),
            endDateTime: formatDateTime(endTime)
        }));
    };

    return (
        <div className="p-6 space-y-8 max-w-7xl mx-auto">
            {/* Header Section */}
            <div className="flex justify-between items-end bg-gray-900/40 p-8 rounded-3xl border border-white/5 backdrop-blur-xl">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse shadow-[0_0_10px_#3b82f6]" />
                        <span className="text-blue-400 font-bold text-xs uppercase tracking-widest">{t('operations.commandCenter')}</span>
                    </div>
                    <h1 className="text-5xl font-black text-white tracking-tighter">{t('operations.title')}</h1>
                    <p className="text-gray-400 mt-2 text-lg">{t('operations.subtitle')}</p>
                </div>
                {canManageOperations && (
                    <button
                        data-testid="new-operation-button"
                        aria-label={t('operations.newOperation')}
                        onClick={() => {
                            console.log('[Operations] NUEVA OPERACIÓN clicked, opening modal');
                            setShowModal(true);
                            generateCode();
                        }}
                        className="group relative px-8 py-4 bg-blue-600 rounded-2xl overflow-hidden transition-all hover:scale-105 active:scale-95 shadow-2xl shadow-blue-500/20"
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        <span className="relative flex items-center gap-3 text-white font-bold text-lg">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" /></svg>
                            {t('operations.newOperationUpper')}
                        </span>
                    </button>
                )}
            </div>

            {/* Stats Overview */}
            {stats && (
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                    {[
                        { label: t('operations.statTotal'), value: stats.total, color: 'gray' },
                        { label: t('operations.statActive'), value: stats.active, color: 'blue' },
                        { label: t('operations.statPlanned'), value: stats.planned, color: 'purple' },
                        { label: t('operations.statLevel3'), value: operations.filter((o: Operation) => o.opcLevel === 'NIVEL_III').length, color: 'red' },
                        { label: t('operations.statCompleted'), value: stats.completed, color: 'green' },
                    ].map((stat, i) => (
                        <div key={i} className="bg-gray-800/50 p-6 rounded-2xl border border-white/5 hover:border-white/10 transition-colors group">
                            <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-1 group-hover:text-gray-300">{stat.label}</div>
                            <div className="text-3xl font-black text-white">{stat.value}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Operations Table */}
            <div className="bg-gray-800/30 rounded-3xl border border-white/5 overflow-hidden backdrop-blur-sm">
                <table className="w-full text-left">
                    <thead>
                        <tr className="bg-white/5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                            <th className="px-6 py-6">{t('operations.colCodeAgency')}</th>
                            <th className="px-6 py-6">{t('operations.colMission')}</th>
                            <th className="px-6 py-6">{t('operations.colPeriod')}</th>
                            <th className="px-6 py-6">{t('operations.colStatus')}</th>
                            <th className="px-6 py-6">{t('operations.colOpcLevel')}</th>
                            <th className="px-6 py-6 text-right">{t('operations.colActions')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                        {operations.map((op: Operation) => {
                            const formatDate = (dateStr: string) => {
                                if (!dateStr) return '-';
                                const d = new Date(dateStr);
                                return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
                            };
                            return (
                                <tr key={op.id} className="hover:bg-white/5 transition-colors group">
                                    <td className="px-6 py-6">
                                        <div className="font-mono text-blue-400 font-bold">{op.code}</div>
                                        <div className="text-xs text-gray-500 font-medium">{op.ownerAgency?.name || (AGENCIES.find(a => a.id === op.agency) ? t(`operations.agencies.${op.agency}`) : undefined)}</div>
                                    </td>
                                    <td className="px-6 py-6">
                                        <div className="text-white font-medium max-w-xs truncate">{op.description}</div>
                                        <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                                            <span className="w-1.5 h-1.5 bg-gray-600 rounded-full" />
                                            {op.eventType}
                                        </div>
                                    </td>
                                    <td className="px-6 py-6">
                                        <div className="text-xs text-gray-300 font-mono">
                                            <div className="flex items-center gap-2">
                                                <span className="text-green-400">▶</span>
                                                {formatDate(op.startDateTime)}
                                            </div>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="text-red-400">■</span>
                                                {formatDate(op.endDateTime)}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-6">
                                        <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider ${op.status === 'ACTIVA' ? 'bg-green-500/10 text-green-400 border border-green-500/20' :
                                            op.status === 'PLANIFICADA' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' :
                                                'bg-gray-500/10 text-gray-400 border border-gray-500/20'
                                            }`}>
                                            {op.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-6">
                                        <div className={`flex items-center gap-2 font-black italic text-sm ${op.opcLevel === 'NIVEL_III' ? 'text-red-400' :
                                            op.opcLevel === 'NIVEL_II' ? 'text-yellow-400' :
                                                'text-green-400'
                                            }`}>
                                            {op.opcLevel?.replace('_', ' ')}
                                        </div>
                                    </td>
                                    <td className="px-6 py-6 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            {canManageOperations ? (
                                                <>
                                                    <button onClick={() => openMembersModal(op)} className="flex items-center gap-1.5 px-3 py-2 bg-indigo-500/10 border border-indigo-500/20 rounded-xl text-[9px] font-black text-indigo-400 uppercase tracking-widest hover:bg-indigo-500/20 transition-all" title={t('operations.manageMembers')}>
                                                        <Users className="w-3.5 h-3.5" /> {t('operations.members')}
                                                    </button>
                                                    <button className="p-3 hover:bg-white/10 rounded-xl text-gray-500 hover:text-white transition-all">
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                                    </button>
                                                </>
                                            ) : (
                                                <div className="text-[10px] font-black text-gray-600 uppercase tracking-widest flex items-center gap-1 justify-end">
                                                    <div className="w-1.5 h-1.5 bg-gray-600 rounded-full" />
                                                    {t('operations.readOnly')}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            {showModal && (
                <div role="dialog" aria-modal="true" data-testid="operation-modal" className="fixed inset-0 z-[100] flex items-center justify-center p-6 backdrop-blur-2xl bg-black/60">
                    <div className="bg-gray-900 w-full max-w-5xl rounded-[3rem] border border-white/10 shadow-[0_0_100px_rgba(0,0,0,0.5)] overflow-hidden animate-in zoom-in-95 duration-300">
                        {/* Modal Header */}
                        <div className="p-10 border-b border-white/5 flex justify-between items-center bg-white/[0.02]">
                            <div>
                                <h2 className="text-3xl font-black text-white tracking-tighter">{t('operations.modalTitle')}</h2>
                                <p className="text-gray-400 font-medium">{t('operations.modalSubtitle')}</p>
                            </div>
                            <button onClick={() => setShowModal(false)} className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 text-white transition-colors">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>

                        <div className="max-h-[70vh] overflow-y-auto p-10 grid grid-cols-3 gap-10 scrollbar-hide">
                            {/* Left Column - General Info */}
                            <div className="col-span-2 space-y-10">
                                {/* Risk Analysis Card */}
                                <div className={`p-8 rounded-[2rem] border-2 transition-all duration-500 scale-100 ${opcResult.color === 'red' ? 'bg-red-500/10 border-red-500/40 shadow-[0_0_40px_rgba(239,68,68,0.2)]' :
                                    opcResult.color === 'yellow' ? 'bg-yellow-500/10 border-yellow-500/40 shadow-[0_0_40px_rgba(234,179,8,0.2)]' :
                                        'bg-green-500/10 border-green-500/40'
                                    }`}>
                                    <div className="flex justify-between items-center text-left">
                                        <div className="flex items-center gap-4">
                                            <div className={`w-3 h-12 rounded-full ${opcResult.color === 'red' ? 'bg-red-500' : opcResult.color === 'yellow' ? 'bg-yellow-500' : 'bg-green-500'}`} />
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <div className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em]">{t('operations.riskResult')}</div>
                                                    <div className="group/tooltip relative">
                                                        <HelpCircle className="w-4 h-4 text-white/40 hover:text-white cursor-help transition-colors" />
                                                        <div className="absolute left-0 top-full mt-3 w-[400px] p-8 bg-[#0a0c10] backdrop-blur-3xl border border-white/20 rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.8)] opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all z-[110] pointer-events-none">
                                                            <div className="flex items-center gap-3 mb-4">
                                                                <div className="w-1 h-6 bg-blue-500 rounded-full" />
                                                                <div className="text-sm font-black text-white uppercase tracking-[0.2em]">{t('operations.methodologyTitle')}</div>
                                                            </div>

                                                            <div className="space-y-6">
                                                                <div className="p-4 bg-white/[0.03] rounded-2xl border border-white/10">
                                                                    <div className="text-[10px] font-black text-gray-500 uppercase mb-2">{t('operations.riskAlgorithm')}</div>
                                                                    <div className="text-lg font-mono text-blue-400 font-bold">Score = (Σ CT1-CT5) × RT_Mult</div>
                                                                    <div className="text-[10px] text-gray-500 mt-2 leading-relaxed">
                                                                        {t('operations.algorithmDesc')}
                                                                    </div>
                                                                </div>

                                                                <div className="space-y-3">
                                                                    <div className="text-[10px] font-black text-gray-500 uppercase tracking-widest">{t('operations.classificationThresholds')}</div>
                                                                    <div className="grid grid-cols-1 gap-2">
                                                                        <div className="flex items-center justify-between p-3 bg-green-500/5 rounded-xl border border-green-500/20">
                                                                            <span className="text-xs font-black text-green-400">{t('operations.thresholdLevel1')}</span>
                                                                            <span className="text-xs font-mono text-green-400/70">0 - 8.9 pts</span>
                                                                        </div>
                                                                        <div className="flex items-center justify-between p-3 bg-yellow-500/5 rounded-xl border border-yellow-500/20">
                                                                            <span className="text-xs font-black text-yellow-400">{t('operations.thresholdLevel2')}</span>
                                                                            <span className="text-xs font-mono text-yellow-400/70">9.0 - 13.9 pts</span>
                                                                        </div>
                                                                        <div className="flex items-center justify-between p-3 bg-red-500/5 rounded-xl border border-red-500/20">
                                                                            <span className="text-xs font-black text-red-400">{t('operations.thresholdLevel3')}</span>
                                                                            <span className="text-xs font-mono text-red-400/70">14.0+ pts</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className={`text-4xl font-black italic tracking-tighter ${opcResult.color === 'red' ? 'text-red-400' : opcResult.color === 'yellow' ? 'text-yellow-400' : 'text-green-400'
                                                    }`}>
                                                    {opcResult.level.replace('_', ' ')}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-3xl font-mono text-white/90">{opcResult.score.toFixed(1)}</div>
                                            <div className="text-[10px] text-gray-500 font-bold tracking-widest uppercase">{t('operations.scoreIndex')}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Form Grid */}
                                <div className="grid grid-cols-2 gap-8 text-left">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">{t('operations.responsibleAgency')}</label>
                                        <select
                                            value={newOp.agency}
                                            onChange={(e) => setNewOp((prev: any) => ({ ...prev, agency: e.target.value }))}
                                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white focus:outline-none focus:border-blue-500 transition-colors appearance-none"
                                        >
                                            {AGENCIES.map(a => <option key={a.id} value={a.id} className="bg-gray-900 text-white">{t(`operations.agencies.${a.id}`)}</option>)}
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">{t('operations.eventType')}</label>
                                        <select
                                            value={newOp.eventType}
                                            onChange={(e) => setNewOp((prev: any) => ({ ...prev, eventType: e.target.value }))}
                                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white focus:outline-none focus:border-blue-500 transition-colors appearance-none"
                                        >
                                            {EVENT_TYPES.map(et => <option key={et.id} value={et.id} className="bg-gray-900 text-white">{t(`operations.eventTypes.${et.id}`)}</option>)}
                                        </select>
                                    </div>

                                    <div className="col-span-2 space-y-2">
                                        <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">{t('operations.missionObjectives')}</label>
                                        <textarea
                                            value={newOp.description}
                                            onChange={(e) => setNewOp((prev: any) => ({ ...prev, description: e.target.value }))}
                                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white focus:outline-none focus:border-blue-500 transition-colors h-24 resize-none"
                                            placeholder={t('operations.missionPlaceholder')}
                                        />
                                    </div>

                                    {/* Date/Time Range Fields */}
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1 flex items-center gap-2">
                                            <span>📅</span> {t('operations.startDateTime')}
                                        </label>
                                        <input
                                            type="datetime-local"
                                            value={newOp.startDateTime}
                                            onChange={(e) => setNewOp((prev: any) => ({ ...prev, startDateTime: e.target.value }))}
                                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white focus:outline-none focus:border-blue-500 transition-colors [color-scheme:dark]"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1 flex items-center gap-2">
                                            <span>📅</span> {t('operations.endDateTime')}
                                        </label>
                                        <input
                                            type="datetime-local"
                                            value={newOp.endDateTime}
                                            onChange={(e) => setNewOp((prev: any) => ({ ...prev, endDateTime: e.target.value }))}
                                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white focus:outline-none focus:border-blue-500 transition-colors [color-scheme:dark]"
                                        />
                                    </div>
                                </div>

                                {/* OPC Matrix Selectors */}
                                <div className="space-y-6 text-left">
                                    <h3 className="text-[10px] font-black text-gray-400 tracking-[0.3em] uppercase opacity-70">{t('operations.ctMatrixTitle')}</h3>
                                    <div className="grid grid-cols-2 gap-8">
                                        {Object.entries(OPC_CRITERIA).map(([key, data]) => (
                                            <div key={key} className="space-y-3">
                                                <div className="flex items-center gap-2">
                                                    <label className="text-xs font-bold text-gray-400">{t(`operations.opc.${key}.label`)}</label>
                                                    <div className="group/cat relative">
                                                        <HelpCircle className="w-3 h-3 text-white/20 hover:text-white/60 cursor-help" />
                                                        <div className="absolute bottom-full left-0 mb-2 w-64 p-3 bg-gray-900 border border-white/10 rounded-xl text-[10px] text-gray-300 font-medium leading-relaxed opacity-0 invisible group-hover/cat:opacity-100 group-hover/cat:visible transition-all z-50 pointer-events-none shadow-2xl">
                                                            {t(`operations.opc.${key}.info`)}
                                                            <div className="absolute top-full left-4 border-8 border-transparent border-t-gray-900" />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-3 gap-2">
                                                    {data.options.map(opt => (
                                                        <div key={opt.value} className="relative group/opt">
                                                            <button
                                                                type="button"
                                                                onClick={() => setNewOp((prev: any) => ({ ...prev, [`${key}${key === 'ct5' ? 'Extension' : key === 'ct4' ? 'Proposito' : key === 'ct3' ? 'Especializacion' : key === 'ct2' ? 'Intensidad' : 'Coordinacion'}`]: opt.value }))}
                                                                className={`w-full p-3 rounded-xl border text-[10px] font-black uppercase transition-all ${(newOp as any)[`${key}${key === 'ct5' ? 'Extension' : key === 'ct4' ? 'Proposito' : key === 'ct3' ? 'Especializacion' : key === 'ct2' ? 'Intensidad' : 'Coordinacion'}`] === opt.value
                                                                    ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-500/20'
                                                                    : 'bg-white/5 border-white/5 text-gray-500 hover:border-white/10 hover:text-white'
                                                                    }`}
                                                            >
                                                                {t(`operations.opc.${key}.options.${opt.value}.label`)}
                                                            </button>
                                                            {/* Mini Tooltip */}
                                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 border border-white/10 rounded-lg text-[9px] text-gray-300 font-bold whitespace-nowrap opacity-0 invisible group-hover/opt:opacity-100 group-hover/opt:visible transition-all z-20 pointer-events-none shadow-xl">
                                                                {t(`operations.opc.${key}.options.${opt.value}.description`)}
                                                                <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-gray-900" />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}

                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2">
                                                <label className="text-xs font-bold text-red-400">{t('operations.rtFactor')}</label>
                                                <div className="group/cat relative">
                                                    <HelpCircle className="w-3 h-3 text-red-400/20 hover:text-red-400/60 cursor-help" />
                                                    <div className="absolute bottom-full left-0 mb-2 w-64 p-3 bg-gray-900 border border-red-500/10 rounded-xl text-[10px] text-gray-300 font-medium leading-relaxed opacity-0 invisible group-hover/cat:opacity-100 group-hover/cat:visible transition-all z-50 pointer-events-none shadow-2xl">
                                                        {t('operations.rtInfo')}
                                                        <div className="absolute top-full left-4 border-8 border-transparent border-t-gray-900" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-3 gap-2">
                                                {['BAJO', 'MEDIO', 'ALTO'].map(rt => (
                                                    <div key={rt} className="relative group/opt">
                                                        <button
                                                            type="button"
                                                            onClick={() => setNewOp((prev: any) => ({ ...prev, rtRelevancia: rt }))}
                                                            className={`w-full p-3 rounded-xl border text-[10px] font-black uppercase transition-all ${newOp.rtRelevancia === rt
                                                                ? 'bg-red-600 border-red-600 text-white'
                                                                : 'bg-white/5 border-white/5 text-gray-500 hover:border-white/10'
                                                                }`}
                                                        >
                                                            {t(`operations.rt.${rt}`)}
                                                        </button>
                                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 border border-white/10 rounded-lg text-[9px] text-gray-300 font-bold whitespace-nowrap opacity-0 invisible group-hover/opt:opacity-100 group-hover/opt:visible transition-all z-20 pointer-events-none shadow-xl">
                                                            {t(`operations.rtTooltip.${rt}`)}
                                                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-gray-900" />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column - Critical Factors & Support */}
                            <div className="space-y-10 text-left">
                                <div className="space-y-4">
                                    <h3 className="text-[10px] font-black text-red-500 tracking-[0.3em] uppercase">{t('operations.criticalFactors')}</h3>
                                    <div className="flex flex-col gap-3">
                                        {[
                                            { id: 'hasMinors', label: t('operations.factorMinors'), icon: '👶' },
                                            { id: 'hasWeapons', label: t('operations.factorWeapons'), icon: '🔫' },
                                            { id: 'isCriticalSector', label: t('operations.factorCriticalSector'), icon: '⚠️' }
                                        ].map(f => (
                                            <button
                                                key={f.id}
                                                onClick={() => setNewOp((prev: any) => ({ ...prev, [f.id]: !prev[f.id] }))}
                                                className={`flex items-center justify-between p-5 rounded-2xl border-2 transition-all ${(newOp as any)[f.id]
                                                    ? 'bg-red-500/10 border-red-500 text-red-400 shadow-xl'
                                                    : 'bg-white/5 border-white/5 text-gray-500 hover:border-white/10'
                                                    }`}
                                            >
                                                <div className="flex items-center gap-4">
                                                    <span className="text-2xl">{f.icon}</span>
                                                    <span className="text-xs font-black uppercase tracking-widest">{f.label}</span>
                                                </div>
                                                {(newOp as any)[f.id] && <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h3 className="text-[10px] font-black text-gray-400 tracking-[0.3em] uppercase opacity-70">{t('operations.supportLogistics')}</h3>
                                    <div className="grid grid-cols-2 gap-3">
                                        {SUPPORT_SERVICES.map(s => (
                                            <button
                                                key={s.id}
                                                onClick={() => setNewOp((prev: any) => ({
                                                    ...prev,
                                                    supportServices: prev.supportServices.includes(s.id)
                                                        ? prev.supportServices.filter((id: string) => id !== s.id)
                                                        : [...prev.supportServices, s.id]
                                                }))}
                                                className={`flex items-center gap-3 p-4 rounded-2xl border transition-all ${newOp.supportServices.includes(s.id)
                                                    ? 'bg-orange-500/10 border-orange-500 text-orange-400'
                                                    : 'bg-white/5 border-white/5 text-gray-500'
                                                    }`}
                                            >
                                                <span className="text-xl">{s.icon}</span>
                                                <span className="text-[9px] font-black uppercase leading-tight">{t(`operations.supportServices.${s.id}`)}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="mt-auto pt-6">
                                    <button
                                        onClick={handleCreate}
                                        disabled={saving}
                                        className="w-full py-6 bg-white text-black font-black text-lg rounded-[2rem] hover:bg-blue-500 hover:text-white transition-all active:scale-95 disabled:opacity-50 shadow-2xl"
                                    >
                                        {saving ? t('operations.processing') : t('operations.confirmMission')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Members Modal */}
            {showMembersModal && selectedOpForMembers && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 backdrop-blur-2xl bg-black/60">
                    <div className="bg-gray-900 w-full max-w-2xl rounded-3xl border border-white/10 shadow-[0_0_100px_rgba(0,0,0,0.5)] overflow-hidden">
                        {/* Header */}
                        <div className="p-8 border-b border-white/5 flex justify-between items-center bg-white/[0.02]">
                            <div>
                                <h2 className="text-xl font-black text-white tracking-tight flex items-center gap-3">
                                    <Users className="w-5 h-5 text-indigo-400" />
                                    {t('operations.membersModalTitle')}
                                </h2>
                                <p className="text-sm text-gray-400 mt-1">
                                    <span className="font-mono text-indigo-400">{selectedOpForMembers.code}</span> — {selectedOpForMembers.description?.substring(0, 60)}
                                </p>
                            </div>
                            <button onClick={() => setShowMembersModal(false)} className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 text-white transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-8 space-y-6 max-h-[60vh] overflow-y-auto">
                            {/* Add member */}
                            <div className="bg-indigo-500/5 border border-indigo-500/15 rounded-2xl p-5">
                                <h3 className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                    <UserPlus className="w-3.5 h-3.5" /> {t('operations.assignUser')}
                                </h3>
                                <div className="space-y-3">
                                    <input
                                        type="text"
                                        placeholder={t('operations.searchUserPlaceholder')}
                                        value={userSearchText}
                                        onChange={(e) => setUserSearchText(e.target.value)}
                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-xs text-white focus:outline-none focus:border-indigo-500 transition-colors"
                                    />
                                    <div className="flex gap-3">
                                        <select
                                            value={newMemberUserId}
                                            onChange={e => setNewMemberUserId(e.target.value)}
                                            className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-indigo-500 transition-colors appearance-none"
                                        >
                                            <option value="" className="bg-gray-900">{t('operations.selectUser')}</option>
                                            {allUsers
                                                .filter(u => !members.some(m => m.userId === u.id))
                                                .filter(u => u.roles?.includes(newMemberRole))
                                                .filter(u => userSearchText === '' ||
                                                    (u.fullName || '').toLowerCase().includes(userSearchText.toLowerCase()) ||
                                                    (u.username || '').toLowerCase().includes(userSearchText.toLowerCase()))
                                                .map(u => (
                                                    <option key={u.id} value={u.id} className="bg-gray-900 text-white">
                                                        {u.fullName || u.username} ({u.username})
                                                    </option>
                                                ))
                                            }
                                        </select>
                                        <select
                                            value={newMemberRole}
                                            onChange={e => setNewMemberRole(e.target.value)}
                                            className="w-40 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-indigo-500 transition-colors appearance-none"
                                        >
                                            <option value="ADMINISTRADOR" className="bg-gray-900 text-white">{t('operations.roleAdmin')}</option>
                                            <option value="COORDINADOR" className="bg-gray-900 text-white">{t('operations.roleCoordinator')}</option>
                                            <option value="SUPERVISOR" className="bg-gray-900 text-white">{t('operations.roleSupervisor')}</option>
                                            <option value="OPERADOR" className="bg-gray-900 text-white">{t('operations.roleOperator')}</option>
                                        </select>
                                        <button
                                            onClick={addMember}
                                            disabled={!newMemberUserId || addingMember}
                                            className="px-5 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-black text-xs uppercase tracking-widest transition-all disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2"
                                        >
                                            <UserPlus className="w-4 h-4" /> {t('operations.assign')}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Members list */}
                            <div>
                                <h3 className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-3">
                                    {t('operations.assignedCount', { count: members.length })}
                                </h3>
                                {loadingMembers ? (
                                    <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
                                ) : members.length === 0 ? (
                                    <div className="text-center py-8 bg-white/[0.02] rounded-2xl border border-white/5">
                                        <Users className="w-8 h-8 text-gray-700 mx-auto mb-2" />
                                        <p className="text-xs text-gray-500 font-bold">{t('operations.noMembers')}</p>
                                        <p className="text-[10px] text-gray-600 mt-1">{t('operations.noMembersHint')}</p>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {members.map(m => (
                                            <div key={m.id} className="flex items-center justify-between p-4 bg-white/[0.02] border border-white/5 rounded-xl hover:border-white/10 transition-colors">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-9 h-9 rounded-xl bg-indigo-600/15 border border-indigo-500/20 flex items-center justify-center text-xs font-black text-indigo-400">
                                                        {(m.fullName || m.username).charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-bold text-white">{m.fullName || m.username}</div>
                                                        <div className="text-[10px] text-gray-500 font-mono">@{m.username}</div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <span className={`text-[9px] font-black px-2.5 py-1 rounded-full uppercase tracking-widest ${m.role === 'ADMINISTRADOR' ? 'bg-red-500/10 text-red-400 border border-red-500/20' :
                                                        m.role === 'COORDINADOR' ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20' :
                                                            m.role === 'SUPERVISOR' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' :
                                                                'bg-green-500/10 text-green-400 border border-green-500/20'
                                                        }`}>{m.role}</span>
                                                    <button
                                                        onClick={() => removeMember(m.userId)}
                                                        className="p-2 hover:bg-red-500/10 rounded-lg text-gray-600 hover:text-red-400 transition-all"
                                                        title={t('operations.remove')}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
